import React, { useRef, useEffect, useState, useCallback } from 'react';
import gsap from 'gsap';

import { useBottomSheet } from './BottomSheetContext';
import { useSelectedServices } from '../../Sections/ServicesSection/SelectedServicesContext';

import Button from '../../Button/Button';

import './bottomSheet.css';

const BottomSheet = ({ cardsData }) => {
    const { isOpen, setIsOpen, currentService, setCurrentService, closeBottomSheet, updateNavigationZIndex, pill } = useBottomSheet();
    const { addService, removeService } = useSelectedServices();
    const sheetRef = useRef(null);
    const contentRef = useRef(null);
    const [isClosing, setIsClosing] = useState(false);

    const animateOpen = useCallback(() => {
        if (!sheetRef.current || !contentRef.current) return;
        const sheet = sheetRef.current;
        const content = contentRef.current;

        gsap.set(content, { y: '100%', opacity: 0 });
        sheet.style.display = 'flex';
        document.body.classList.add('bottom-sheet-open');

        const tl = gsap.timeline();
        tl.to(sheet, { duration: 0.3, opacity: 1, ease: 'power2.out' })
          .to(content, { 
              duration: 0.5, 
              y: '0%', 
              opacity: 1, 
              ease: 'power3.out',
              onComplete: () => updateNavigationZIndex(true)
          }, '-=0.1');
    }, [updateNavigationZIndex]);

    const animateClose = useCallback(() => {
        if (!sheetRef.current || !contentRef.current) return;
        const sheet = sheetRef.current;
        const content = contentRef.current;

        const tl = gsap.timeline({
            onComplete: () => {
                sheet.style.display = 'none';
                document.body.classList.remove('bottom-sheet-open');
                setIsClosing(false);
                updateNavigationZIndex(false);
            setIsOpen(false);
            }
        });

        tl.to(content, { duration: 0.5, y: '100%', opacity: 0, ease: 'power3.in' })
          .to(sheet, { duration: 0.3, opacity: 0, ease: 'power2.in' }, '-=0.2');
    }, [updateNavigationZIndex, setIsOpen]);
    useEffect(() => {
        if (isOpen && !isClosing) {
            animateOpen();
        } else if (!isOpen && !isClosing) {
            animateClose();
        }
    }, [isOpen, isClosing, animateOpen, animateClose]);

    const closeSheet = useCallback(() => {
        setIsClosing(true);
        animateClose();
    }, [animateClose]);

    const handleButtonClick = () => {
        if (currentService.isSelected) {
            removeService(currentService.text);
        } else {
            addService(currentService.text);
        }
        setCurrentService({
            ...currentService,
            isSelected: !currentService.isSelected
        });
        closeSheet();
        };

    const handleClick = useCallback((event) => {
        if (sheetRef.current && contentRef.current && !contentRef.current.contains(event.target) && isOpen && !isClosing) {
            closeSheet();
        }
    }, [isOpen, isClosing, closeSheet]);

    const handleKeyDown = useCallback((event) => {
        if (event.key === 'Escape' && !isClosing) {
            closeSheet();
        }
    }, [isClosing, closeSheet]);

    useEffect(() => {
        if (isOpen) {
            document.addEventListener('mousedown', handleClick);
            document.addEventListener('keydown', handleKeyDown);
        }
        return () => {
            document.removeEventListener('mousedown', handleClick);
            document.removeEventListener('keydown', handleKeyDown);
};
    }, [isOpen, handleClick, handleKeyDown]);

    if (!isOpen) return null;

    const backgroundImage = cardsData && cardsData[0].imageSrc ? `url(${cardsData[0].imageSrc})` : '';

    return (
        <div
            className={`bottom-sheet ${isOpen ? 'open' : ''}`}
            ref={sheetRef}
        >
            {currentService && (
                <div className="bottom-sheet-content" ref={contentRef}>
                    <button className="close-button" onClick={() => closeSheet()}>
                        <span className="material-symbols-outlined">close</span>
                    </button>
                    <div className="imageheader-wrapper">
                        <img className="service-image" src={currentService.imageSrc}></img>
                        <div className="service-header-gradient"> 
                            <h3 className="service-header" >{currentService.text}</h3>
                        </div>
                    </div>
                    <div className="bottomsheet-content">
                        <p>{currentService.description}</p>
                        {currentService.footnote && <p className="footnote">{currentService.footnote}</p>}
                    </div>
                    <div className="callout">
                        <p className="label-bottomsheet" >Wil je meer weten over deze expertise? <br></br>Selecteer de knop hieronder en stel je vraag.</p>
                        <div className="bottomSheet-button-wrapper">
                            <Button
                                variant="primary"
                                icon={currentService.isSelected ? 'check' : ''}
                                className={`select-button ${currentService.isSelected ? 'selected-button' : ''}`}
                                style={{
                                    backgroundColor: currentService.isSelected
                                        ? 'var(--color-brand-primary)'
                                        : '',
                                    color: currentService.isSelected
                                        ? 'var(--color-neutral-0)'
                                        : '',
                                }}
                                onClick={handleButtonClick}
                            >
                                {currentService.isSelected ? 'Geselecteerd' : 'Ik wil meer weten'}
                            </Button>
                        </div>
                    </div>
                </div>
            )}
            <div className="background-image" style={{ backgroundImage: !backgroundImage || cardsData[0].imageSrc === '' ? 'none' : backgroundImage, backgroundSize: 'cover', height: '200px' }} />
        </div>
    );
};

export default BottomSheet;
